/*
 * Copyright 2026 North Carolina State University
 */

import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, MenuItem, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { COLUMN_TYPES, FIELD_TYPES, columnTypeInfo, newId, typeInfo } from '../fieldTypes';
import { useLookup } from '../api';

const lines = (text) => text.split('\n').map(s => s.trim()).filter(Boolean);
const commas = (text) => text.split(',').map(s => s.trim()).filter(Boolean);

const toDraft = (field) => ({
  ...field,
  optionsText: (field.options || []).join('\n'),
  unitsText: (field.units || []).join(', '),
  columns: (field.columns || []).map(c => ({
    ...c,
    optionsText: (c.options || []).join(', '),
    unitsText: (c.units || []).join(', '),
  })),
});

const fromDraft = (d) => {
  const info = typeInfo(d.type);
  return {
    id: d.id,
    label: d.label.trim(),
    type: d.type,
    required: !!d.required,
    help: (d.help || '').trim(),
    width: d.width || info.width || 'half',
    options: info.hasOptions || info.hasSuggestions ? lines(d.optionsText) : [],
    units: info.hasUnits ? commas(d.unitsText) : [],
    multiple: info.hasMultiple ? !!d.multiple : false,
    roles: info.hasRoles ? (d.roles || []) : [],
    project_field: d.type === 'chamber' ? (d.project_field || null) : null,
    default_today: info.hasDefaultToday ? !!d.default_today : false,
    autofill: d.type === 'number' && d.autofill?.source && d.autofill?.attribute ? d.autofill : null,
    columns: info.hasColumns
      ? d.columns.filter(c => c.label.trim()).map(c => ({
        id: c.id,
        label: c.label.trim(),
        type: c.type,
        options: columnTypeInfo(c.type).hasOptions ? commas(c.optionsText) : [],
        units: columnTypeInfo(c.type).hasUnits ? commas(c.unitsText) : [],
      }))
      : [],
  };
};

/** Add or edit one field of a form. */
const FieldEditorDialog = ({ open, field, isNew, formHasEntries, onSave, onClose, autofillSources = [], controlAttributes = [], projectFields = [] }) => {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const roles = useLookup('roles', open);

  useEffect(() => {
    if (open && field) { setD(toDraft(field)); setError(''); }
  }, [open, field]);

  if (!d || !field) return null;
  const info = typeInfo(d.type);
  const set = (patch) => setD(prev => ({ ...prev, ...patch }));
  const setCol = (i, patch) => set({ columns: d.columns.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });

  const save = () => {
    const out = fromDraft(d);
    if (!out.label) return setError('Give the field a label.');
    if (info.hasOptions && !out.options.length) return setError('Add at least one option.');
    if (info.hasColumns && !out.columns.length) return setError('Add at least one column.');
    onSave(out);
  };

  const typeChanged = !isNew && field.type !== d.type;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? 'Add field' : 'Edit field'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Label" value={d.label} autoFocus required
            onChange={e => set({ label: e.target.value })} />
          <TextField select label="Type" value={d.type}
            onChange={e => set({ type: e.target.value, width: typeInfo(e.target.value).width || 'half' })}>
            {FIELD_TYPES.map(t => <MenuItem key={t.type} value={t.type}>{t.label}</MenuItem>)}
          </TextField>
          {typeChanged && formHasEntries && (
            <Alert severity="warning">
              Earlier entries keep the value they were saved with. Changing the type only affects new entries,
              so pick a type the old values still make sense in.
            </Alert>
          )}

          {info.hasOptions && (
            <TextField label="Options (one per line)" multiline minRows={3} value={d.optionsText}
              onChange={e => set({ optionsText: e.target.value })} />
          )}
          {info.hasSuggestions && (
            <TextField label="Suggestions (optional, one per line)" multiline minRows={3} value={d.optionsText}
              helperText="Offered while typing; any other value can still be entered."
              onChange={e => set({ optionsText: e.target.value })} />
          )}
          {d.type === 'number' && (
            <Box className="ipm-autofill">
              <FormControlLabel
                control={<Switch checked={!!d.autofill} disabled={!autofillSources.length || !controlAttributes.length}
                  onChange={e => set({ autofill: e.target.checked ? { source: autofillSources[0]?.value || '', attribute: controlAttributes[0]?.value || '' } : null })} />}
                label="Fill in automatically from the List of Controls"
              />
              {(!autofillSources.length || !controlAttributes.length) && (
                <Typography variant="body2" className="ipm-muted">
                  Needs a Control field (or a table column of type Control) on this form, and a number field on the List of Controls form.
                </Typography>
              )}
              {d.autofill && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                  <TextField select size="small" label="From the controls picked in" value={d.autofill.source} sx={{ flex: 1, minWidth: 200 }}
                    onChange={e => set({ autofill: { ...d.autofill, source: e.target.value } })}>
                    {autofillSources.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" label="Use the largest" value={d.autofill.attribute} sx={{ flex: 1, minWidth: 200 }}
                    onChange={e => set({ autofill: { ...d.autofill, attribute: e.target.value } })}>
                    {controlAttributes.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                </Box>
              )}
            </Box>
          )}
          {info.hasUnits && (
            <TextField label="Units (optional, comma-separated)" placeholder="e.g. gallons, liters, mL"
              helperText="If set, the person entering data picks one of these next to the number."
              value={d.unitsText} onChange={e => set({ unitsText: e.target.value })} />
          )}
          {info.hasMultiple && (
            <FormControlLabel control={<Switch checked={!!d.multiple} onChange={e => set({ multiple: e.target.checked })} />}
              label={`Allow more than one ${info.label.toLowerCase()}`} />
          )}
          {d.type === 'chamber' && (
            <TextField select label="Only list compartments active on the project picked in" value={d.project_field || ''}
              onChange={e => set({ project_field: e.target.value || null })} disabled={!projectFields.length}
              helperText={projectFields.length ? 'Leave as “All compartments” to list every compartment.' : 'Add a Project field to this form to use this.'}>
              <MenuItem value=""><em>All compartments</em></MenuItem>
              {projectFields.map(f => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
            </TextField>
          )}
          {info.hasRoles && (
            <Autocomplete
              multiple options={roles} getOptionLabel={r => r.name}
              value={roles.filter(r => (d.roles || []).includes(r._id))}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              onChange={(e, v) => set({ roles: v.map(r => r._id) })}
              renderInput={params => (
                <TextField {...params} label="Only list users with these institution roles"
                  helperText="Leave empty to list every active user. A name can still be typed in for someone without an account." />
              )}
            />
          )}
          {info.hasDefaultToday && (
            <FormControlLabel control={<Switch checked={!!d.default_today} onChange={e => set({ default_today: e.target.checked })} />}
              label={d.type === 'date' ? 'Fill in today’s date by default' : 'Fill in the current time by default'} />
          )}

          {info.hasColumns && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Columns</Typography>
              {d.columns.map((c, i) => {
                const cInfo = columnTypeInfo(c.type);
                return (
                  <Box key={c.id} className="ipm-column-row">
                    <TextField size="small" label="Column label" value={c.label} sx={{ flex: 2 }}
                      onChange={e => setCol(i, { label: e.target.value })} />
                    <TextField size="small" select label="Type" value={c.type} sx={{ flex: 1, minWidth: 110 }}
                      onChange={e => setCol(i, { type: e.target.value })}>
                      {COLUMN_TYPES.map(t => <MenuItem key={t.type} value={t.type}>{t.label}</MenuItem>)}
                    </TextField>
                    {cInfo.hasOptions && (
                      <TextField size="small" label="Options (comma-separated)" value={c.optionsText} sx={{ flex: 2 }}
                        onChange={e => setCol(i, { optionsText: e.target.value })} />
                    )}
                    {cInfo.hasUnits && (
                      <TextField size="small" label="Units (comma-separated)" value={c.unitsText} sx={{ flex: 2 }}
                        onChange={e => setCol(i, { unitsText: e.target.value })} />
                    )}
                    <Tooltip title="Remove column">
                      <IconButton aria-label="Remove column" onClick={() => set({ columns: d.columns.filter((_, idx) => idx !== i) })}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
              <Button size="small" startIcon={<AddIcon />}
                onClick={() => set({ columns: [...d.columns, { id: newId('c'), label: '', type: 'text', optionsText: '', unitsText: '' }] })}>
                Add column
              </Button>
            </Box>
          )}

          <TextField label="Help text (optional)" value={d.help || ''} helperText="Shown under the field."
            onChange={e => set({ help: e.target.value })} />
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <FormControlLabel control={<Switch checked={!!d.required} onChange={e => set({ required: e.target.checked })} />} label="Required" />
            <TextField select size="small" label="Width" value={d.width || 'half'} sx={{ minWidth: 160 }}
              onChange={e => set({ width: e.target.value })}>
              <MenuItem value="half">Half width</MenuItem>
              <MenuItem value="full">Full width</MenuItem>
            </TextField>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save}>{isNew ? 'Add field' : 'Done'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FieldEditorDialog;
